<script setup lang="ts">
import { computed, provide, ref } from 'vue'
import { useComponentId } from '../../composables/useComponentId'
import { analyticsNameProp } from '../common/props'
import { OenUiAlertKey } from './symbol.const'
import { themes, type Themes } from './types'

const props = defineProps({
  theme: {
    default: themes[0],
    type: String,
    validator: (value: Themes) => {
      return themes.indexOf(value) > -1
    },
  },
  ...analyticsNameProp,
})

const alertTheme = computed(() => {
  return `oen-ui-alert--${props.theme}`
})
const id = useComponentId('alert')
provide(OenUiAlertKey, ref({ id }))
</script>

<template>
  <div
    :id="id"
    class="oen-ui-alert"
    role="alert"
    :class="alertTheme"
    :analytics-name="analyticsName + '-' + id"
  >
    <!-- @slot default: For the alert content -->
    <slot />
  </div>
</template>

<style scoped>
.oen-ui-alert {
  @apply flex justify-between items-center box-border rounded-md py-2 px-3 border;
  @apply type-caption text-primary-text;
}

.oen-ui-alert--dark {
  @apply text-light bg-dark-bg border-gray-light;

  :deep(.oen-ui-alert-action) {
    &:focus {
      @apply bg-white-dim;
    }
  }
}

.oen-ui-alert--red {
  @apply bg-red-lighter border-red-lighter;

  :deep(.oen-ui-alert-action) {
    @apply text-red-text;

    &:hover,
    &:focus {
      @apply bg-red-lighter;
    }
  }
}

.oen-ui-alert--orange,
.oen-ui-alert--primary {
  @apply bg-oen-ui-primary-lighter border-oen-ui-primary-lighter;

  :deep(.oen-ui-alert-action) {
    @apply text-oen-ui-primary-text;

    &:hover,
    &:focus {
      @apply bg-oen-ui-primary-lighter;
    }
  }
}

.oen-ui-alert--yellow {
  @apply bg-yellow-lighter border-yellow-lighter;

  :deep(.oen-ui-alert-action) {
    @apply text-yellow-text;

    &:hover,
    &:focus {
      @apply bg-yellow-lighter;
    }
  }
}

.oen-ui-alert--green {
  @apply bg-green-lighter border-green-lighter;

  :deep(.oen-ui-alert-action) {
    @apply text-green-text;

    &:hover,
    &:focus {
      @apply bg-green-lighter;
    }
  }
}

.oen-ui-alert--blue {
  @apply bg-blue-lighter border-blue-lighter;

  :deep(.oen-ui-alert-action) {
    @apply text-blue-text;

    &:hover,
    &:focus {
      @apply bg-blue-lighter;
    }
  }
}

.oen-ui-alert--purple {
  @apply bg-purple-lighter border-purple-lighter;

  :deep(.oen-ui-alert-action) {
    @apply text-purple-text;

    &:hover,
    &:focus {
      @apply bg-purple-lighter;
    }
  }
}

.oen-ui-alert--pink {
  @apply bg-pink-lighter border-pink-lighter;

  :deep(.oen-ui-alert-action) {
    @apply text-pink-text;

    &:hover,
    &:focus {
      @apply bg-pink-lighter;
    }
  }
}

.oen-ui-alert--gray {
  @apply bg-gray-lighter border-gray-lighter;

  :deep(.oen-ui-alert-action) {
    @apply text-secondary-text;

    &:hover,
    &:focus {
      @apply bg-gray-light;
    }
  }
}
</style>
